// Tile API service for vector tile handling
const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

class TileService {
  /**
   * Get tile URL with authentication
   * @param z - Zoom level
   * @param x - Tile X coordinate
   * @param y - Tile Y coordinate
   * @param format - Tile format (default: 'pbf' for vector tiles)
   * @returns Authenticated tile URL
   */
  getTileUrl(z: number, x: number, y: number, format: 'png' | 'pbf' = 'pbf'): string {
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    const url = `${baseURL}/tiles/${z}/${x}/${y}.${format}`;
    
    if (token) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
    
    return url;
  }

  /**
   * Get tile metadata
   * @returns Tile service metadata
   */
  async getTileMetadata(): Promise<any> {
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    
    try {
      const response = await fetch(`${baseURL}/api/tiles/metadata`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tile metadata: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching tile metadata:', error);
      throw error;
    }
  }

  /**
   * Get MapLibre vector tile source configuration
   * @returns Vector tile source configuration for MapLibre GL JS
   */
  getVectorTileSource(): any {
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    
    const source = {
      type: 'vector',
      tiles: [`${baseURL}/tiles/{z}/{x}/{y}.pbf`],
      minzoom: 6,
      maxzoom: 18,
      bounds: [72.034520, 22.731381, 72.695637, 23.251274], // From metadata
      // Promote the 'id' property to be the feature ID for feature-state
      promoteId: { 'road_network': 'id' },
    };

    // No authentication required to tiles if token exists
    source.tiles = [`${baseURL}/tiles/{z}/{x}/{y}.pbf`];

    return source;
  }

  /**
   * Query features at a specific location
   * @param lat - Latitude
   * @param lng - Longitude
   * @param zoom - Current zoom level (optional)
   * @returns Features at the location
   */
  async queryFeatures(lat: number, lng: number, zoom?: number): Promise<any[]> {
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    
    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lng: lng.toString(),
        ...(zoom && { zoom: zoom.toString() })
      });

      const response = await fetch(`${baseURL}/api/features/query?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return []; // No features found
        }
        throw new Error(`Failed to query features: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error querying features:', error);
      return []; // Return empty array on error
    }
  }

  /**
   * Get tile bounds/extent information
   * @returns Tile service bounds
   */
  async getTileBounds(): Promise<{
    minZoom: number;
    maxZoom: number;
    bounds: [number, number, number, number]; // [west, south, east, north]
  }> {
    try {
      const metadata = await this.getTileMetadata();
      return {
        minZoom: metadata.minzoom || 6,
        maxZoom: metadata.maxzoom || 18,
        bounds: metadata.bounds || [72.0, 22.5, 73.5, 24.0] // Default Ahmedabad area
      };
    } catch (error) {
      console.error('Error fetching tile bounds:', error);
      // Return default values for Ahmedabad area
      return {
        minZoom: 6,
        maxZoom: 18,
        bounds: [72.0, 22.5, 73.5, 24.0]
      };
    }
  }

  /**
   * Create authenticated headers for tile requests
   * @returns Headers object with authentication
   */
  getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token') || localStorage.getItem('auth_token');
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }
}

export const tileService = new TileService();